import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckType } from '@/types'
import { getDepartmentConfig } from '@/services/storage'

const ChevronIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-400 flex-shrink-0">
    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface CheckTypeCardProps {
  onClick: () => void
  iconBg: string
  icon: React.ReactNode
  title: string
  subtitle: string
  hoverBorder: string
}

const CheckTypeCard: React.FC<CheckTypeCardProps> = ({ onClick, iconBg, icon, title, subtitle, hoverBorder }) => (
  <button
    onClick={onClick}
    className={`w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow border-2 border-transparent ${hoverBorder} active:opacity-80`}
  >
    <div className="flex items-center">
      <div className={`w-14 h-14 ${iconBg} rounded-lg flex items-center justify-center mr-3 flex-shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-base font-medium text-gray-800">{title}</div>
        <div className="text-xs text-gray-500 mt-0.5 leading-snug">{subtitle}</div>
      </div>
      <ChevronIcon />
    </div>
  </button>
)

export const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const deptConfig = getDepartmentConfig()

  const go = (type: CheckType) => navigate(`/check/${type}`)

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 顶部标题栏 */}
      <div className="bg-red-700 text-white px-4 py-5">
        <h1 className="text-xl font-bold text-center">燃气安全检查</h1>
        <p className="text-red-200 text-center text-sm mt-0.5">移动端检查系统</p>
      </div>

      <div className="flex-1 px-4 py-5 space-y-6">

        {/* 区块一：商业用户检查 */}
        <div>
          <div className="flex items-center mb-3">
            <div className="w-1 h-4 bg-red-600 rounded mr-2" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">商业用户检查</h2>
          </div>
          <div className="space-y-3">
            <CheckTypeCard
              onClick={() => go(CheckType.LIQUEFIED_GAS)}
              iconBg="bg-red-50"
              hoverBorder="hover:border-red-200"
              title="瓶装液化石油气"
              subtitle="检查液化气钢瓶、阀门、软管等设备"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <rect x="12" y="18" width="24" height="28" rx="4" fill="#c62828" />
                  <rect x="18" y="8" width="12" height="12" rx="2" fill="#e53935" />
                  <circle cx="24" cy="12" r="2" fill="#ffcdd2" />
                  <rect x="20" y="2" width="8" height="6" rx="1" fill="#b71c1c" />
                  <rect x="16" y="24" width="16" height="4" rx="1" fill="#ffcdd2" opacity="0.6" />
                </svg>
              }
            />
            <CheckTypeCard
              onClick={() => go(CheckType.NATURAL_GAS)}
              iconBg="bg-orange-50"
              hoverBorder="hover:border-orange-200"
              title="管道天然气"
              subtitle="检查燃气管道、计量表、燃烧器具等"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <path d="M4 24h40" stroke="#ff9800" strokeWidth="4" strokeLinecap="round" />
                  <path d="M8 24v-8a4 4 0 014-4h4" stroke="#ff9800" strokeWidth="4" strokeLinecap="round" />
                  <path d="M36 24v-8a4 4 0 00-4-4h-4" stroke="#ff9800" strokeWidth="4" strokeLinecap="round" />
                  <circle cx="16" cy="12" r="3" fill="#ffb74d" />
                  <circle cx="32" cy="12" r="3" fill="#ffb74d" />
                </svg>
              }
            />
          </div>
        </div>

        {/* 区块二：燃气场站检查 */}
        <div>
          <div className="flex items-center mb-3">
            <div className="w-1 h-4 bg-blue-600 rounded mr-2" />
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">燃气场站检查</h2>
          </div>
          <div className="space-y-3">
            <CheckTypeCard
              onClick={() => go(CheckType.LPG_DEPOT)}
              iconBg="bg-blue-50"
              hoverBorder="hover:border-blue-200"
              title="液化石油气储配站"
              subtitle="储罐区、装卸区、灌装区等设施检查"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <ellipse cx="24" cy="28" rx="14" ry="10" fill="#1565c0" />
                  <ellipse cx="24" cy="22" rx="14" ry="10" fill="#1976d2" />
                  <ellipse cx="24" cy="22" rx="14" ry="4" fill="#42a5f5" />
                  <rect x="22" y="10" width="4" height="8" rx="1" fill="#90caf9" />
                  <rect x="18" y="8" width="12" height="4" rx="1" fill="#1565c0" />
                </svg>
              }
            />
            <CheckTypeCard
              onClick={() => go(CheckType.LNG_GASIFICATION)}
              iconBg="bg-cyan-50"
              hoverBorder="hover:border-cyan-200"
              title="LNG 气化站"
              subtitle="低温储罐、气化装置、调压系统检查"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <rect x="10" y="14" width="16" height="26" rx="8" fill="#0097a7" />
                  <rect x="12" y="12" width="12" height="6" rx="2" fill="#00bcd4" />
                  <path d="M26 24h8M26 30h6" stroke="#4dd0e1" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="36" cy="24" r="2" fill="#4dd0e1" />
                  <path d="M20 38v4M16 38v4M24 38v4" stroke="#00acc1" strokeWidth="2" strokeLinecap="round" />
                </svg>
              }
            />
            <CheckTypeCard
              onClick={() => go(CheckType.LNG_FILLING)}
              iconBg="bg-indigo-50"
              hoverBorder="hover:border-indigo-200"
              title="LNG 加气站"
              subtitle="储罐区、卸车区、加气机等设施检查"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="12" width="18" height="28" rx="4" fill="#3949ab" />
                  <rect x="10" y="10" width="14" height="6" rx="2" fill="#5c6bc0" />
                  <rect x="28" y="20" width="12" height="16" rx="2" fill="#3949ab" />
                  <path d="M26 28h2M26 32h4" stroke="#9fa8da" strokeWidth="1.5" strokeLinecap="round" />
                  <rect x="30" y="36" width="8" height="4" rx="1" fill="#283593" />
                </svg>
              }
            />
            <CheckTypeCard
              onClick={() => go(CheckType.GATE_PRESSURE)}
              iconBg="bg-teal-50"
              hoverBorder="hover:border-teal-200"
              title="天然气门站/调压站"
              subtitle="进出站管道、调压装置、安全阀等检查"
              icon={
                <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
                  <path d="M6 24h36" stroke="#00695c" strokeWidth="4" strokeLinecap="round" />
                  <rect x="18" y="16" width="12" height="16" rx="3" fill="#00897b" />
                  <circle cx="24" cy="24" r="4" fill="#4db6ac" />
                  <path d="M14 24v-6M34 24v-6" stroke="#00695c" strokeWidth="2" strokeLinecap="round" />
                  <rect x="11" y="14" width="6" height="4" rx="1" fill="#00695c" />
                  <rect x="31" y="14" width="6" height="4" rx="1" fill="#00695c" />
                </svg>
              }
            />
          </div>
        </div>

        {/* 历史记录入口 */}
        <button
          onClick={() => navigate('/history')}
          className="w-full bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-gray-500 mr-2">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-gray-600 text-sm">查看历史记录</span>
        </button>
      </div>

      {/* 底部信息 */}
      <div className="text-center py-4 text-sm text-gray-400">
        <p>{deptConfig?.departmentName || '城市管理局'}</p>
        <p className="text-xs mt-1">燃气安全监督检查专用</p>
        <button onClick={() => navigate('/onboarding')} className="text-xs text-gray-300 mt-2 hover:text-gray-400">
          修改部门信息
        </button>
      </div>
    </div>
  )
}
